import { useState } from "react";
import { X } from "lucide-react";
import { FadeIn } from "@/components/FadeIn";
import { Ornament } from "@/components/Ornament";
import { useLanguage } from "@/context/LanguageContext";
import { strings } from "@/i18n";

// Structure prête pour la galerie du 24 septembre 2026 :
// [{ title: "La soirée", photos: ["/galerie/soiree/001.jpg", ...], videos: ["/galerie/soiree/film.mp4"] }]
const albums = [];

const Gallery = ({ albums }) => {
  const [open, setOpen] = useState(null);
  return (
    <div className="mx-auto max-w-6xl px-6 pb-24 md:px-8" data-testid="galerie-albums">
      {albums.map((album) => (
        <section key={album.title} className="mt-16">
          <h2 className="text-center font-serif text-3xl font-medium text-noir">{album.title}</h2>
          <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3 md:gap-4">
            {album.photos.map((src) => (
              <button key={src} type="button" onClick={() => setOpen(src)} className="double-frame block">
                <img src={src} alt={album.title} className="aspect-square w-full object-cover" loading="lazy" />
              </button>
            ))}
          </div>
          {album.videos.map((src) => (
            <video key={src} src={src} controls className="mt-6 w-full" />
          ))}
        </section>
      ))}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-noir/90 p-4" onClick={() => setOpen(null)} data-testid="galerie-lightbox">
          <button type="button" aria-label="Fermer" className="absolute right-5 top-5 text-ivory">
            <X size={28} strokeWidth={1.5} />
          </button>
          <img src={open} alt="" className="max-h-full max-w-full object-contain" />
        </div>
      )}
    </div>
  );
};

export default function PhotosVideos() {
  const { lang } = useLanguage();
  const s = strings.photos[lang];

  return (
    <div data-testid="photos-page">
      <section className="mx-auto max-w-4xl px-6 pt-16 md:px-8 md:pt-24">
        <FadeIn>
          <div className="text-center">
            <p className="eyebrow" data-testid="photos-eyebrow">
              {s.eyebrow}
            </p>
            <h1 className="mt-6 font-serif text-4xl font-medium tracking-[0.12em] text-noir sm:text-5xl" data-testid="photos-title">
              {s.title}
            </h1>
            <p className="mt-6 font-serif text-xl italic tracking-[0.2em] text-bordeaux md:text-2xl" data-testid="photos-subtitle">
              {s.subtitle}
            </p>
            <div className="mt-10">
              <Ornament />
            </div>
          </div>
        </FadeIn>

        {albums.length === 0 && (
          <FadeIn delay={0.1}>
            <div className="double-frame my-16 md:my-20" data-testid="photos-attente-frame">
              <div className="double-frame-inner px-6 py-16 text-center md:px-12 md:py-24">
                <p className="mx-auto max-w-xl font-serif text-2xl italic leading-relaxed text-noir/80 [text-wrap:pretty] md:text-3xl" data-testid="photos-attente-text">
                  {s.text}
                </p>
                <p className="mt-8 font-sans text-[11px] uppercase tracking-[0.3em] text-noir/40" data-testid="photos-attente-note">
                  {s.note}
                </p>
              </div>
            </div>
          </FadeIn>
        )}
      </section>

      {albums.length > 0 && <Gallery albums={albums} />}
    </div>
  );
}
